import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import API from "../../api";
import { capture } from "../../instrument";
import { getSetting } from "../../queries/setting";

const DataEntrySection = () => {
  const queryClient = useQueryClient();

  const { data: setting } = useQuery({ queryKey: ["setting"], queryFn: getSetting });

  const settingMutation = useMutation({
    mutationFn: (patch) => API.post("/setting", patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: ["setting"] });
      queryClient.setQueryData(["setting"], (cached) => ({ ...cached, ...patch }));
    },
    onSuccess: ({ data }) => queryClient.setQueryData(["setting"], data),
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur d'enregistrement");
      queryClient.invalidateQueries({ queryKey: ["setting"] });
    },
  });

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Saisie</h2>
        <p className="mt-1.5 text-sm text-gray-500">Aides à la saisie des dossiers, appliquées à tous les utilisateurs.</p>
      </div>

      {!setting && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {setting && (
        <div className="divide-y divide-gray-100 rounded-xl bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
          <div className="flex items-center justify-between gap-6 py-3.5">
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-gray-900">Autocomplétion médecin</p>
              <p className="mt-0.5 text-[13px] text-gray-500">Propose les médecins connus lors de la saisie d'un prescripteur.</p>
            </div>
            <span className="relative inline-flex h-5 w-9 shrink-0 rounded-full bg-gray-200 p-0.5 transition-colors has-[:checked]:bg-primary-500 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2">
              <input
                type="checkbox"
                role="switch"
                name="doctorAutocomplete"
                aria-label="Autocomplétion médecin"
                className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-full focus:outline-none"
                checked={setting.autocomplete.doctor}
                onChange={(e) => settingMutation.mutate({ autocomplete: { ...setting.autocomplete, doctor: e.target.checked } })}
              />
              <span className="pointer-events-none size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
            </span>
          </div>
          <div className="flex items-center justify-between gap-6 py-3.5">
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-gray-900">Forçage de codes</p>
              <p className="mt-0.5 text-[13px] text-gray-500">Permet d'ajouter manuellement un code absent du référentiel sur une prescription.</p>
            </div>
            <span className="relative inline-flex h-5 w-9 shrink-0 rounded-full bg-gray-200 p-0.5 transition-colors has-[:checked]:bg-primary-500 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2">
              <input
                type="checkbox"
                role="switch"
                name="allowForcedCodes"
                aria-label="Forçage de codes"
                className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-full focus:outline-none"
                checked={setting.allowForcedCodes !== false}
                onChange={(e) => settingMutation.mutate({ allowForcedCodes: e.target.checked })}
              />
              <span className="pointer-events-none size-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEntrySection;
